import { defineStore } from 'pinia';
import { useHeadGenerate } from '~/composables/useHeadGenerate';
import {
  createPopulateString,
  parseItemInCollection,
} from '~/utils/strapiComponents/addStrapiData';
import { filterDateActiveItem } from '~/utils/strapiComponents/filters';
import { useStoreRoot } from '../abstract/root';
import { useStoreCountry } from '../client/country';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import type { ICategoryData, ICategoryStrapiResponse } from '~/types/categories';
import type { ISsGame } from '~/types/softswiss/ssGames';
import type { IStrapiImage } from '~/types/strapiImage';

interface ICategoryPageData {
  gamesCount: number;
  gamesMeta: { total: number };
  gamesPerPage: number;
  games: ISsGame[];
  providersCount: Record<string, number>;
}

export const parseCategory = (itemData: ICategoryData, page: boolean = false): ICategoryData => {
  const geo = useStoreRoot().geo;
  const item: ICategoryData = parseItemInCollection(JSON.parse(JSON.stringify(itemData)));

  if (geo && geo !== 'main') {
    const mirrorHead = item.mirrorHead?.find((head) => head?.geo === geo);
    const { getMirrorCanonical } = useHeadGenerate();

    if (mirrorHead) {
      item.descriptionHiddenText = '';
      item.descriptionText = '';
      item.head.title = mirrorHead.title;
      item.head.description = mirrorHead.description || '';
      item.head.nofollow = mirrorHead.nofollow || false;
      item.head.noindex = mirrorHead.noindex || false;
      item.head.canonical = getMirrorCanonical(mirrorHead);

      if (mirrorHead.noindex || mirrorHead.nofollow) {
        item.head.alternates = [];
      }
    }

    const mirrorBody = item.mirrorBody?.find((body) => body?.geo === geo);

    if (mirrorBody) {
      item.descriptionText = mirrorBody.descriptionText;
    }
  }

  item.isPage = page;
  return item;
};

export const useStoreCategoryPage = defineStore('categoryPage', {
  state: () => ({
    categories: [] as ICategoryData[],
    specialCategory: '',
    defaultCategory: 'allGames',
    categoryDataList: {} as Record<string, ICategoryPageData>,
    categoryScrollPosition: null as null | number,
    requestPromise: null as Promise<ICategoryStrapiResponse> | null,
  }),

  getters: {
    list(state) {
      return state.categories || [];
    },
    activeList(): ICategoryData[] {
      const storeRoot = useStoreRoot();
      const storeSsPlayer = useStoreSsPlayer();

      return this.list
        .filter((item) => item.locale === storeRoot.userLang)
        .filter(filterDateActiveItem)
        .filter((item) =>
          storeSsPlayer.hasAuthToken || storeSsPlayer.isPlayerAuthorised
            ? !item.hideForAuthorized
            : !item.hideForGuests
        );
    },
    activeSelectorCategories(): ICategoryData[] {
      return this.activeList.filter((category) => !!category.showInSelector);
    },
    activeSpecials(): ICategoryData[] {
      return this.activeList.filter((category) => !!category.showInSpecial);
    },
    activeLabels(): ICategoryData[] {
      return this.activeList.filter((category) => category.labelType !== 'no');
    },
    gameCategories(): ICategoryData[] {
      return this.activeSelectorCategories.filter(
        (category) => category.categoryId !== 'favorites' && category.categoryId !== 'allGames'
      );
    },
    gameRowsCategories(): { id: string; name: string; icon?: IStrapiImage }[] {
      const storeRoot = useStoreRoot();
      const storeCountry = useStoreCountry();

      const gamesRowsFiltered = this.activeList
        .filter((itemCategory) => {
          if (!itemCategory.showGamesRow) {
            return false;
          }
          if (
            COUNTRYS_WITH_BANNED_LIVE.includes(storeCountry.country.toLowerCase()) &&
            itemCategory.categoryId === 'live'
          ) {
            return false;
          }

          if (storeRoot.isEstonianLicense && itemCategory.categoryId === 'btc-games') {
            return false;
          }

          return true;
        })
        .map((category): any => {
          return {
            id: category.categoryId,
            name: category.categoryTitle,
            icon: category.categoryMenuIcon,
          };
        });

      return [
        ...gamesRowsFiltered,
        {
          id: 'explore-more',
          name: 'explore-more',
          games: [],
          position: 0,
          games_count: 0,
        },
        {
          id: 'providers',
          name: 'Providers',
          games: [],
          position: 0,
          games_count: 0,
        },
        {
          id: 'collections',
          name: 'Collections',
          games: [],
          position: 0,
          games_count: 0,
        },
      ];
    },
    allAvailableCategoies(): ICategoryData[] {
      const storeSsPlayer = useStoreSsPlayer();
      const storeCountry = useStoreCountry();
      const storeRoot = useStoreRoot();
      return this.activeList.filter((category) => {
        if (!storeSsPlayer.isPlayerAuthorised && category.categoryId === 'favorites') {
          return false;
        }
        if (
          category.categoryId === 'live' &&
          COUNTRYS_WITH_BANNED_LIVE.includes(storeCountry.country.toLowerCase())
        ) {
          return false;
        }

        if (storeRoot.isEstonianLicense && category.categoryId === 'btc-games') {
          return false;
        }

        return true;
      });
    },
    sidebarCategories(): ICategoryData[] {
      const sidebarCategories = this.allAvailableCategoies.filter((category) =>
        Boolean(category.showInSidebar)
      );

      // if category is "recommended games" - move it to the start of array
      // sidebarCategories.sort((a, b) => {
      //   if (a.categoryId === 'recommended-games') {
      //     return -1;
      //   }
      //   if (b.categoryId === 'recommended-games') {
      //     return 1;
      //   }
      //   return 0;
      // });

      return sidebarCategories;
    },
    mainPageSelectorCategories(): ICategoryData[] {
      return this.allAvailableCategoies.filter((category) => !!category.showInSelector);
    },
    populateString() {
      return createPopulateString([
        'categoryIcon',
        'head',
        'head.alternates',
        'breadcrumbs',
        'breadcrumbs.itemImage',
        'mirrorHead',
        'mirrorBody',
        'categoryMenuIcon',
      ]);
    },
  },

  actions: {
    async loadCategories() {
      if (!this.requestPromise) {
        this.requestPromise = strapi()
          .get('category-pages-short')
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        this.ADD_DATA_FROM_STRAPI(response.data);

        return true;
      } else {
        return false;
      }
    },
    ADD_DATA_FROM_STRAPI(data: ICategoryData[]) {
      const parsedData = data
        .map((item) => (item.categoryId ? parseCategory(item) : ({} as ICategoryData)))
        .filter(
          (parsedItem) =>
            !this.categories.some(
              ({ categoryId }) => categoryId === parsedItem.categoryId && parsedItem.isPage
            )
        );

      const filteredState = this.categories.filter(
        (item) => !parsedData.find(({ categoryId }) => categoryId && categoryId === item.categoryId)
      );

      this.categories = [...filteredState, ...parsedData].sort((a, b) =>
        a.position - b.position > -1 ? 1 : -1
      );
    },
    ADD_OR_EDIT_ONE_FROM_STRAPI(page: ICategoryData) {
      const parsedCategory = parseCategory(page, true);
      const index = this.categories.findIndex((p) => p.categoryId === parsedCategory.categoryId);

      if (index > -1) {
        this.categories.splice(index, 1, parsedCategory);
      } else {
        this.categories.push(parsedCategory);
      }
    },
    setSpecial(specialCategory: string) {
      this.specialCategory = specialCategory;
    },
    setCategoryDataList(pageName: string, data: ICategoryPageData) {
      this.categoryDataList[pageName] = data;
    },
    setCategoryScrollPosition(value: number) {
      this.categoryScrollPosition = value;
    },
  },
});
